import React, { useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import Avatar from "@/components/Avatar";
import StatusBadge from "@/components/StatusBadge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowRightLeft, ArrowRight } from "lucide-react";
import { computeEvenSplit, computeAfterCounts, nextAssignees } from "@/lib/rebalance";

function MemberSelect({ value, onValueChange, members, placeholder, className, testid }) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger className={className} data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function TaskRow({ task, checked, onToggle, target, onTarget, members }) {
  return (
    <div className="flex items-center gap-2.5 px-3 py-2.5 border-b border-zinc-100 last:border-0" data-testid={`rebalance-task-${task.task_id}`}>
      <input type="checkbox" checked={checked} onChange={onToggle} className="accent-zinc-900 h-4 w-4" data-testid={`rebalance-check-${task.task_id}`} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate">{task.name}</p>
        <p className="text-xs text-zinc-400 truncate">{task.builder ? `${task.builder} · ` : ""}{task.project_name}</p>
      </div>
      <StatusBadge status={task.status} />
      <MemberSelect value={target || ""} onValueChange={onTarget} members={members}
        placeholder="Keep" className="rounded-sm h-8 w-36 shrink-0" testid={`rebalance-target-${task.task_id}`} />
    </div>
  );
}

function PreviewPanel({ preview, personId }) {
  if (preview.length === 0) return null;
  return (
    <div className="border border-zinc-200 rounded-sm" data-testid="rebalance-preview">
      <div className="px-3 py-2 border-b border-zinc-100 bg-zinc-50 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
        Open tasks — before → after
      </div>
      <div className="p-3 space-y-2">
        {preview.map((r) => {
          const down = r.after < r.before;
          return (
            <div key={r.uid} className="flex items-center gap-3 text-sm" data-testid={`rebalance-preview-${r.uid}`}>
              <Avatar id={r.uid} name={r.name} size={22} />
              <span className="flex-1 truncate">{r.name}{r.uid === personId && <span className="text-xs text-zinc-400"> (overloaded)</span>}</span>
              <span className="text-zinc-400 tabular-nums">{r.before}</span>
              <ArrowRight className="h-3.5 w-3.5 text-zinc-300" />
              <span className={`font-heading font-black tabular-nums ${down ? "text-emerald-600" : "text-rose-600"}`}>{r.after}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function WorkloadRebalance({ person, perPerson = {}, open, onOpenChange, onDone }) {
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [targets, setTargets] = useState({});   // task_id -> target user_id
  const [selected, setSelected] = useState([]); // task_ids ticked for bulk-apply
  const [bulkTarget, setBulkTarget] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !person) return;
    setTargets({}); setSelected([]); setBulkTarget("");
    setLoading(true);
    Promise.all([
      api.get("/tasks", { params: { assignee: person.user_id } }),
      api.get("/users"),
    ]).then(([t, u]) => {
      setTasks((t.data || []).filter((x) => x.status !== "completed"));
      setMembers((u.data || []).filter((m) => m.user_id !== person.user_id && m.active !== false));
    }).catch((e) => toast.error(apiErr(e, "Could not load tasks")))
      .finally(() => setLoading(false));
  }, [open, person]);

  const nameMap = useMemo(() => {
    const m = { [person?.user_id]: person?.name };
    members.forEach((x) => { m[x.user_id] = x.name; });
    return m;
  }, [members, person]);

  const toggleSel = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const allOn = tasks.length > 0 && selected.length === tasks.length;
  const toggleAll = () => setSelected(allOn ? [] : tasks.map((t) => t.task_id));
  const setTarget = (id, v) => setTargets((p) => ({ ...p, [id]: v }));

  const applyToSelected = () => {
    if (!bulkTarget || selected.length === 0) return;
    setTargets((prev) => {
      const next = { ...prev };
      selected.forEach((id) => { next[id] = bulkTarget; });
      return next;
    });
    setSelected([]); setBulkTarget("");
  };

  const assignedCount = useMemo(() => Object.values(targets).filter(Boolean).length, [targets]);

  const balanceEvenly = () => {
    if (!person || members.length === 0 || tasks.length === 0) return;
    const { targets: next, reason } = computeEvenSplit(person, members, tasks, perPerson);
    if (reason === "no-pool") { toast.info("No delivery teammates to balance across"); return; }
    setTargets(next);
    if (reason === "balanced") toast.info("Already balanced — no moves needed");
    else toast.success(`Suggested moving ${Object.keys(next).length} task(s) — review & confirm`);
  };

  const preview = useMemo(() => {
    if (!person || assignedCount === 0) return [];
    const { after, movedTargets } = computeAfterCounts(person, tasks, targets, perPerson);
    return [person.user_id, ...movedTargets].map((uid) => ({
      uid, name: nameMap[uid] || "—", before: perPerson[uid] || 0, after: after[uid],
    }));
  }, [tasks, targets, perPerson, person, assignedCount, nameMap]);

  const confirm = async () => {
    const toMove = tasks.filter((t) => targets[t.task_id]);
    if (toMove.length === 0) return;
    setSaving(true);
    let moved = 0;
    const failed = [];
    const usedTargets = new Set();
    for (const t of toMove) {
      const tgt = targets[t.task_id];
      try {
        await api.patch(`/tasks/${t.task_id}`, { assigned_to: nextAssignees(t, person.user_id, tgt) });
        moved += 1; usedTargets.add(tgt);
      } catch (e) {
        failed.push(t.name);
      }
    }
    setSaving(false);
    if (moved > 0) {
      toast.success(`Reassigned ${moved} task${moved === 1 ? "" : "s"} across ${usedTargets.size} teammate${usedTargets.size === 1 ? "" : "s"}`);
      onDone && onDone();
    }
    if (failed.length > 0) {
      toast.error(`Couldn't move ${failed.length}: ${failed.slice(0, 3).join(", ")}${failed.length > 3 ? "…" : ""}`);
    }
    if (failed.length === 0) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-2xl w-[calc(100vw-2rem)] sm:w-full max-h-[92vh] overflow-y-auto" data-testid="rebalance-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ArrowRightLeft className="h-4 w-4" /> Rebalance {person?.name?.split(" ")[0]}&apos;s workload
          </DialogTitle>
          <DialogDescription>Assign different open tasks to different teammates, then confirm all at once.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="flex justify-end">
            <Button size="sm" variant="outline" onClick={balanceEvenly} disabled={loading || tasks.length === 0 || members.length === 0}
              className="rounded-sm gap-1.5 text-xs" data-testid="rebalance-balance-evenly">
              <ArrowRightLeft className="h-3.5 w-3.5" /> Balance evenly
            </Button>
          </div>
          {/* bulk-apply helper */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 bg-zinc-50 border border-zinc-200 rounded-sm px-3 py-2.5">
            <span className="text-xs font-semibold text-zinc-500 shrink-0">{selected.length} selected →</span>
            <MemberSelect value={bulkTarget} onValueChange={setBulkTarget} members={members}
              placeholder="Assign selected to…" className="rounded-sm h-8 flex-1" testid="rebalance-bulk-target" />
            <Button size="sm" className="h-8 rounded-sm bg-zinc-900 hover:bg-zinc-800 shrink-0"
              disabled={!bulkTarget || selected.length === 0} onClick={applyToSelected} data-testid="rebalance-apply-selected">
              Apply to selected
            </Button>
          </div>

          {/* task list */}
          <div className="border border-zinc-200 rounded-sm">
            <div className="flex items-center gap-2.5 px-3 py-2 border-b border-zinc-100 bg-zinc-50">
              <input type="checkbox" checked={allOn} onChange={toggleAll} className="accent-zinc-900 h-4 w-4" data-testid="rebalance-select-all" />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                {loading ? "Loading…" : `${tasks.length} open task${tasks.length === 1 ? "" : "s"}`}
              </span>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {!loading && tasks.length === 0 && <p className="p-4 text-sm text-zinc-400">No open tasks — nothing to move.</p>}
              {tasks.map((t) => (
                <TaskRow key={t.task_id} task={t} members={members}
                  checked={selected.includes(t.task_id)} onToggle={() => toggleSel(t.task_id)}
                  target={targets[t.task_id]} onTarget={(v) => setTarget(t.task_id, v)} />
              ))}
            </div>
          </div>

          {/* before → after preview */}
          <PreviewPanel preview={preview} personId={person?.user_id} />
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="rebalance-confirm" onClick={confirm} disabled={saving || assignedCount === 0}
            className="bg-zinc-900 hover:bg-zinc-800 rounded-sm gap-1.5">
            <ArrowRightLeft className="h-4 w-4" />
            {saving ? "Moving…" : `Confirm ${assignedCount || ""}`.trim()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
